import { useState, useEffect } from 'react';
import { IonContent, IonModal, IonSpinner, IonButtons, IonButton, IonHeader, IonToolbar } from '@ionic/react';
import { Trans, useTranslation } from 'react-i18next';
import {
  loginWithEmailAndPassword,
  checkExists,
  register,
} from '../../services/authService';
import { useAuthStore } from '../../store/useAuthStore';
import { AccessCodeModal } from './AccessCodeModal';
import { HeaderLanguageButton } from '../ui/HeaderLanguageButton';
import { detectEmailOrPhoneId } from '../../utils/emailOrPhoneId';
import { formatPhoneInput, toE164 } from '../../utils/phoneNumber';
import { getDevice } from '../../services/deviceService';
import { setFocus } from '../../utils/focus';
import { getShareReferral } from '../../pages/ShareRedirectPage';

type SignInStep = 'identify' | 'email-login' | 'phone-login' | 'register';

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * @deprecated Currently a no-op. This used to bias the header title
   * ("Sign In" vs "Sign Up") at the identify step, but the modal header no
   * longer shows a title (it's now the language selector + Close). Kept so
   * existing callers still typecheck; re-wire here if a title ever returns.
   */
  initialMode?: 'signIn' | 'signUp';
  /**
   * Optional context line shown above the form (all steps). Used by the
   * logged-out publish flow to explain why we're asking them to sign up now
   * ("One step away!…").
   */
  intro?: string;
}

export const SignInModal: React.FC<SignInModalProps> = ({ isOpen, onClose, intro }) => {
  // Bound to the `auth` namespace; cross-namespace lookups use the
  // `common:` prefix (Cancel, Back, Continue, etc. live in common.json).
  const { t } = useTranslation('auth');
  const { setAuthenticated } = useAuthStore();

  const [step, setStep] = useState<SignInStep>('identify');
  const [contactInput, setContactInput] = useState('');
  const [existsData, setExistsData] = useState<{ exists: boolean; userId?: string; profileId?: string } | null>(null);

  const [password, setPassword] = useState('');
  const [stayLoggedIn, setStayLoggedIn] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [displayNameDirty, setDisplayNameDirty] = useState(false);
  const [bio, setBio] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [accessCodeModal, setAccessCodeModal] = useState<{ open: boolean; mode: 'email' | 'sms' }>({ open: false, mode: 'email' });

  // Phone-signup white-screen fix (targeted patch — see docs in
  // handleIdentify + handleIonModalDismissed below). When the phone-signup
  // path enters the SMS code-verify flow, we hide the outer IonModal EARLY
  // so its animation completes well before the eventual auth-success →
  // route-redirect races. Without this, the IonModal was mid-dismiss when
  // RegistrationGate unmounted the parent tree, leaving stuck DOM that
  // occluded the next page (user saw a blank page; refresh recovered).
  // AccessCodeModal is rendered as a fragment sibling of the IonModal, not
  // nested inside it, so hiding the IonModal doesn't take it down too.
  // Backlog: unify SignInModal off IonModal entirely (Path A).
  const [hideIonModal, setHideIonModal] = useState(false);

  useEffect(() => {
    if (!displayNameDirty) {
      setDisplayName(`${firstName} ${lastName}`.trim());
    }
  }, [firstName, lastName, displayNameDirty]);

  useEffect(() => {
    if (step === 'phone-login') {
      setAccessCodeModal({ open: true, mode: 'sms' });
    }
  }, [step]);

  const resetAll = () => {
    setStep('identify');
    setContactInput('');
    setExistsData(null);
    setPassword('');
    setStayLoggedIn(false);
    setFirstName('');
    setLastName('');
    setRegPassword('');
    setDisplayName('');
    setDisplayNameDirty(false);
    setBio('');
    setAgreedToTerms(false);
    setError('');
    setLoading(false);
    setAccessCodeModal({ open: false, mode: 'email' });
    setHideIonModal(false);
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const handleSignedIn = () => {
    setAuthenticated(true);
    handleClose();
  };

  // Guard for IonModal's onDidDismiss. Fires whenever the IonModal's `isOpen`
  // flips false — including our deliberate "hide early to hand off to
  // AccessCodeModal" flip. In that case, DON'T tear down SignInModal state:
  // AccessCodeModal is still the active flow (rendered as a fragment sibling)
  // and needs SignInModal mounted to keep providing its props + handlers.
  // Only fire the full handleClose when the dismiss is a genuine close
  // (backdrop tap, iOS swipe-down) — i.e. hideIonModal is still false.
  const handleIonModalDismissed = () => {
    if (hideIonModal) return;
    handleClose();
  };

  const detectedType = detectEmailOrPhoneId(contactInput);

  const handleIdentify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (detectedType === 'unknown') {
      setError(t('identify.errorInvalid'));
      return;
    }

    setLoading(true);
    try {
      const contact = detectedType === 'email'
        ? { email: contactInput.trim() }
        : { phone: toE164(contactInput)! };
      const referral = getShareReferral();
      const result = await checkExists(contact, referral?.ref);
      setExistsData(result);

      if (detectedType === 'phone') {
        // Hide the outer IonModal NOW so its dismiss animation finishes
        // well BEFORE the eventual auth-success → route-redirect race that
        // otherwise leaves stuck ion-modal DOM behind (blank screen bug).
        setHideIonModal(true);
        setStep('phone-login');
      } else if (result.exists) {
        setStep('email-login');
        setFocus('password', 250, true);
      } else {
        setStep('register');
        setFocus('firstName', 250, true);
      }
    } catch {
      setError(t('common:toast.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await loginWithEmailAndPassword(contactInput.trim(), password, stayLoggedIn);
      handleSignedIn();
    } catch {
      setError(t('emailLogin.errorInvalid'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(existsData!.userId!, existsData!.profileId!, {
        firstName,
        lastName,
        displayName,
        password: regPassword,
        ...(bio.trim() ? { bio: bio.trim() } : {}),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        identifier: getDevice()?.identifier || '',
        termAck: agreedToTerms,
      });
      handleSignedIn();
    } catch {
      setError(t('register.errorFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAccessCodeSuccess = () => {
    setAccessCodeModal({ open: false, mode: 'email' });
    handleSignedIn();
  };

  const goBack = () => {
    setStep('identify');
    setPassword('');
    setError('');
    setFirstName('');
    setLastName('');
    setRegPassword('');
    setDisplayName('');
    setDisplayNameDirty(false);
    setBio('');
    setAgreedToTerms(false);
    setExistsData(null);
    // Re-show the outer IonModal in case we came here from the phone-signup
    // hand-off (which hid it). Harmless no-op on other back-navigation paths.
    setHideIonModal(false);
  };

  return (
    <>
    <IonModal isOpen={isOpen && !hideIonModal} onDidDismiss={handleIonModalDismissed}>
      <IonHeader>
        <IonToolbar>
          {/* No title — language selector upper-left, Close upper-right. The
              language button opens an IonPopover (an Ionic root-level overlay),
              which is why it can live in the toolbar despite the toolbar
              clipping overflow: a plain dropdown would be cut off here. */}
          <IonButtons slot="start">
            <HeaderLanguageButton triggerId="signin-language-trigger" />
          </IonButtons>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>{t('common:actions.close')}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="flex items-center justify-center min-h-full">
          <div className="w-full max-w-sm">
            {intro && (
              <p className="mb-5 text-center text-sm font-medium text-foreground">{intro}</p>
            )}
            {step === 'identify' && (
              <form onSubmit={handleIdentify} className="flex flex-col gap-4">
                <div>
                  <label htmlFor="contact" className="block text-sm font-medium text-foreground mb-1">
                    {t('identify.label')}
                  </label>
                  <input
                    id="contact"
                    data-testid="auth-contact-input"
                    type="text"
                    value={contactInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (detectEmailOrPhoneId(val) === 'phone') {
                        setContactInput(formatPhoneInput(val));
                      } else {
                        setContactInput(val);
                      }
                    }}
                    required
                    autoComplete="email tel"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    placeholder={t('identify.placeholder')}
                  />
                </div>
                {error && <p className="text-brand-danger text-sm text-center">{error}</p>}
                <button
                  type="submit"
                  data-testid="auth-continue"
                  disabled={loading || detectedType === 'unknown'}
                  className="w-full py-2 px-4 bg-brand-primary text-white font-medium rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <IonSpinner name="crescent" className="w-4 h-4" />
                      {t('identify.checking')}
                    </>
                  ) : (
                    t('common:actions.continue')
                  )}
                </button>
              </form>
            )}

            {step === 'email-login' && (
              <>
                <form onSubmit={handleEmailLogin} className="flex flex-col gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">{t('emailLogin.emailLabel')}</label>
                    <input
                      type="email"
                      value={contactInput}
                      readOnly
                      className="w-full px-3 py-2 border border-border rounded-lg bg-muted text-muted-foreground"
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1">
                      {t('emailLogin.passwordLabel')}
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      placeholder={t('emailLogin.passwordPlaceholder')}
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={stayLoggedIn}
                      onChange={(e) => setStayLoggedIn(e.target.checked)}
                      className="w-4 h-4 rounded border-border text-brand-primary focus:ring-brand-primary"
                    />
                    <span className="text-sm text-muted-foreground">{t('emailLogin.stayLoggedIn')}</span>
                  </label>
                  {error && <p className="text-brand-danger text-sm text-center">{error}</p>}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2 px-4 bg-brand-primary text-white font-medium rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <IonSpinner name="crescent" className="w-4 h-4" />
                        {t('emailLogin.signingIn')}
                      </>
                    ) : (
                      t('emailLogin.signInCta')
                    )}
                  </button>
                </form>
                <div className="mt-3 flex flex-col items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setAccessCodeModal({ open: true, mode: 'email' })}
                    className="text-sm text-brand-primary hover:underline"
                  >
                    {t('emailLogin.useAccessCodeCta')}
                  </button>
                  <button
                    type="button"
                    onClick={goBack}
                    className="text-sm text-brand-primary hover:underline"
                  >
                    {t('emailLogin.usePhoneCta')}
                  </button>
                </div>
              </>
            )}

            {step === 'register' && (
              <>
                <p className="text-sm text-muted-foreground mb-4">
                  <Trans
                    t={t}
                    i18nKey="register.intro"
                    values={{ contact: contactInput }}
                    components={{ strong: <span className="font-medium" /> }}
                  />
                </p>
                <form onSubmit={handleRegister} className="flex flex-col gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-foreground mb-1">
                      {t('fields.firstName')}
                    </label>
                    <input
                      id="firstName"
                      data-testid="signup-first-name"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-foreground mb-1">
                      {t('fields.lastName')}
                    </label>
                    <input
                      id="lastName"
                      data-testid="signup-last-name"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="regPassword" className="block text-sm font-medium text-foreground mb-1">
                      {t('fields.password')}
                    </label>
                    <input
                      id="regPassword"
                      data-testid="signup-password"
                      type="password"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="displayName" className="block text-sm font-medium text-foreground mb-1">
                      {t('fields.displayName')}
                    </label>
                    <input
                      id="displayName"
                      data-testid="signup-display-name"
                      type="text"
                      value={displayName}
                      onChange={(e) => {
                        setDisplayName(e.target.value);
                        setDisplayNameDirty(true);
                      }}
                      required
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="regBio" className="block text-sm font-medium text-foreground mb-1">
                      {t('fields.bio')} <span className="text-muted-foreground font-normal">{t('fields.bioOptionalSuffix')}</span>
                    </label>
                    <textarea
                      id="regBio"
                      data-testid="signup-bio"
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      maxLength={500}
                      rows={3}
                      placeholder={t('fields.bioPlaceholder')}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none"
                    />
                    <p className="mt-1 text-xs text-muted-foreground text-right">{bio.length}/500</p>
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="signup-terms"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      required
                      className="mt-0.5 w-4 h-4 rounded border-border text-brand-primary focus:ring-brand-primary"
                    />
                    <span className="text-sm text-muted-foreground">
                      {t('terms.agreePrefix')}{' '}
                      <a
                        href="https://www.homeplated.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-primary hover:underline"
                      >
                        {t('terms.linkText')}
                      </a>
                    </span>
                  </label>
                  {error && <p className="text-brand-danger text-sm text-center">{error}</p>}
                  <button
                    type="submit"
                    data-testid="signup-submit"
                    disabled={loading || !agreedToTerms}
                    className="w-full py-2 px-4 bg-brand-primary text-white font-medium rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <IonSpinner name="crescent" className="w-4 h-4" />
                        {t('register.submitting')}
                      </>
                    ) : (
                      t('register.submitCta')
                    )}
                  </button>
                </form>
                <div className="mt-3 flex justify-center">
                  <button
                    type="button"
                    onClick={goBack}
                    className="text-sm text-brand-primary hover:underline"
                  >
                    {t('common:actions.back')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

      </IonContent>
    </IonModal>
    {/* Sibling of the IonModal (not nested inside IonContent), so hiding
        the IonModal early during phone-signup does NOT take AccessCodeModal
        down with it. See `hideIonModal` state + handleIdentify phone branch. */}
    <AccessCodeModal
      isOpen={accessCodeModal.open}
      onClose={() => {
        setAccessCodeModal({ open: false, mode: 'email' });
        if (step === 'phone-login') {
          // Cancel from phone-signup code-verify: reset step + re-show the
          // outer IonModal so user lands back on the identify step.
          setStep('identify');
          setHideIonModal(false);
        }
      }}
      onLoginSuccess={handleAccessCodeSuccess}
      mode={accessCodeModal.mode}
      initialEmail={detectedType === 'email' ? contactInput.trim() : ''}
      initialPhone={detectedType === 'phone' ? contactInput : ''}
      stayLoggedIn={stayLoggedIn}
    />
    </>
  );
};
